import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, forwardRef, HostBinding, Input, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ProjectAttachment, ProjectAttachmentFilters } from 'src/app/commons/models/project-attachment.model';
import * as ProjectAttachmentActions from 'src/app/store/actions/project-attachment.actions';
import { AppState } from 'src/app/store/reducers';
import * as ProjectAttachmentSelectors from 'src/app/store/selectors/project-attachment.selectors';


export const CURRENCY_FIELD_MAT_FORM_FIELD_CONTROL: any = {
  provide: MatFormFieldControl,
  useExisting: forwardRef(() => ProjectAttachmentFieldComponent),
};

@Component({
  selector: "app-project-attachment-field",
  templateUrl: "./project-attachment-field.component.html",
  styleUrls: ["./project-attachment-field.component.scss"],
  providers: [CURRENCY_FIELD_MAT_FORM_FIELD_CONTROL],
  host: {
    "[class.floating]": "shouldLabelFloat",
    "[id]": "id",
  },
})
export class ProjectAttachmentFieldComponent
  implements ControlValueAccessor, MatFormFieldControl<ProjectAttachment> {
  private unsubscribe$ = new Subject<void>();
  private _value: ProjectAttachment;
  private _disabled = false;

  private _required = false;

  get errorState(): boolean {
    return !this.ngControl.valid;
  }
  focused: boolean; //TODO
  controlType?: string = "project-attachment-selection-field";
  autofilled?: boolean;
  projectAttachmentAriaDescribedBy?: string;


  @Input()
  projectAttachmentFilters: ProjectAttachmentFilters;

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    @Optional() @Self() public ngControl: NgControl
  ) {

    this.actions$
      .pipe(
        ofType(ProjectAttachmentActions.projectAttachmentSelected),
        takeUntil(this.unsubscribe$),
        map(({ projectAttachment }) => (projectAttachment ? new ProjectAttachment(projectAttachment) : null))

      )
      .subscribe((projectAttachment) => (this.value = projectAttachment));

    this.store$.pipe(
      select(ProjectAttachmentSelectors.getFilters),
      takeUntil(this.unsubscribe$),
    ).subscribe(
      filters => this.projectAttachmentFilters = filters
    );

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }
  stateChanges = new Subject<void>();
  id: string;


  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  get empty() {
    return !this.value;
  }

  @HostBinding("class.floating")
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get required() {
    return this._required || this.ngControl?.control?.hasValidator(Validators.required) || false;
  }

  @Input()
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    //TODO
  }
  onContainerClick(event: MouseEvent): void {
    if (!this.disabled) {
      if ((event.target as Element).tagName.toLowerCase() != "mat-icon") {
        this.selectProjectAttachment();
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.stateChanges.complete();
  }

  selectProjectAttachment() {
    this.store$.dispatch(ProjectAttachmentActions.selectProjectAttachment({ filters: this.projectAttachmentFilters }));
  }

  clearProjectAttachment() {
    this.value = null;
  }

  set value(value: ProjectAttachment) {
    this._value = value;
    this.notifyValueChange();
    this.stateChanges.next();
  }

  get value(): ProjectAttachment {
    return this._value;
  }

  onChange: (value) => {};
  onTouched: () => {};

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);
    }
  }

  writeValue(obj: ProjectAttachment): void {
    this._value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
